import React, { useState, useEffect, useRef } from 'react';
import { X, Compass, RefreshCw } from 'lucide-react';
import { useUser } from '../hooks/useUser';
import { useLearningMode } from '../contexts/LearningModeContext';
import { getExerciseSuggestion, type AdaptiveSuggestion } from '../services/api';
import { trackEvent } from '../utils/analytics';

/**
 * B10 — Suggestion adaptative d'exercice (PASSIVE, sans appel IA) :
 * calculée côté serveur depuis l'historique de scores du mode courant.
 * Deux cas : « À retravailler » (concept < 60 % de réussite, ≥ 2 tentatives)
 * ou « À découvrir » (premier concept du parcours jamais pratiqué).
 * Un clic relance le chemin de génération existant (handleLearningPathExercise).
 * Dismiss mémorisé par utilisateur ET par jour : la carte revient le lendemain.
 */

interface AdaptiveSuggestionCardProps {
  onStartNodeExercise: (nodeId: string) => void;
  isGenerating: boolean;
}

const dismissKey = (userId: string) => {
  const day = new Date().toISOString().slice(0, 10); // YYYY-MM-DD (local suffirait, UTC évite les ambiguïtés)
  return `adaptive_suggestion_dismissed_${userId}_${day}`;
};

const AdaptiveSuggestionCard: React.FC<AdaptiveSuggestionCardProps> = ({ onStartNodeExercise, isGenerating }) => {
  const { user } = useUser();
  const { learningMode } = useLearningMode();
  const [suggestion, setSuggestion] = useState<AdaptiveSuggestion | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const shownTrackedRef = useRef(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    // C4 — repartir de zéro à chaque changement de mode (ou d'utilisateur). Sinon, si le
    // nouveau mode ne renvoie aucune suggestion, celle du mode précédent reste affichée et
    // son bouton pointe un nodeId qui n'existe pas dans le nouveau mode (bouton mort). On
    // réinitialise aussi le flag d'analytics pour re-compter l'affichage de la nouvelle carte.
    setSuggestion(null);
    shownTrackedRef.current = false;

    getExerciseSuggestion(user.id, learningMode)
      .then(result => {
        if (cancelled || !result) return;
        setSuggestion(result);
      })
      .catch(() => {
        // Best-effort : pas de carte si l'appel échoue, l'accueil reste fonctionnel
      });

    return () => { cancelled = true; };
  }, [user, learningMode]);

  const visible = Boolean(
    user &&
    suggestion &&
    suggestion.nodeId &&
    !localStorage.getItem(dismissKey(user.id)) &&
    !dismissed
  );

  useEffect(() => {
    if (visible && !shownTrackedRef.current) {
      shownTrackedRef.current = true;
      trackEvent('adaptive_suggestion_shown', { learningMode, type: suggestion!.type, concept: suggestion!.concept });
    }
  }, [visible, learningMode, suggestion]);

  if (!visible || !user || !suggestion || !suggestion.nodeId) return null;

  const handleDismiss = () => {
    localStorage.setItem(dismissKey(user.id), '1');
    setDismissed(true);
  };

  const handleClick = () => {
    trackEvent('adaptive_suggestion_clicked', { learningMode, type: suggestion.type, concept: suggestion.concept });
    onStartNodeExercise(suggestion.nodeId!);
  };

  const isReview = suggestion.type === 'review';

  return (
    <div className="glass dark:glass-dark rounded-xl p-5 relative overflow-hidden animate-fade-in">
      <div className={`absolute top-0 right-0 w-48 h-48 ${isReview ? 'bg-orange-500/10' : 'bg-emerald-500/10'} rounded-full blur-3xl -mr-24 -mt-24 pointer-events-none`}></div>

      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors z-10"
        title="Masquer cette suggestion"
        aria-label="Masquer cette suggestion"
      >
        <X className="w-4 h-4" />
      </button>

      {/* pr-9 réserve la colonne de la croix (top-3 right-3, 28 px) : en ligne, le bouton
          « Lancer un exercice » est collé au bord droit et passait sous la croix. */}
      <div className="relative flex flex-col sm:flex-row sm:items-center gap-4 pr-9">
        <div className="flex-1 min-w-0">
          <div className="flex items-center mb-1">
            {isReview
              ? <RefreshCw className="w-5 h-5 mr-2 text-orange-500 shrink-0" />
              : <Compass className="w-5 h-5 mr-2 text-emerald-500 shrink-0" />}
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {isReview ? 'À retravailler' : 'À découvrir'} : {suggestion.conceptLabel}
            </h2>
          </div>
          <p className="text-sm text-gray-600 dark:text-slate-300">
            {suggestion.reason}
            {isReview && ' — un nouvel exercice ciblé peut débloquer ce concept.'}
          </p>
        </div>

        <button
          onClick={handleClick}
          disabled={isGenerating}
          className={`shrink-0 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
            isReview
              ? 'bg-orange-600 hover:bg-orange-700'
              : 'bg-emerald-600 hover:bg-emerald-700'
          }`}
        >
          {isGenerating ? 'Génération…' : 'Lancer un exercice'}
        </button>
      </div>
    </div>
  );
};

export default AdaptiveSuggestionCard;
